import { z } from 'zod';

/**
 * Shared field primitives, so validation of the same concept cannot drift
 * between modules.
 */

/**
 * FEAT-001 / BR-003 — email is the unique login identifier across all companies.
 * RFC-valid, ≤254 characters, no consecutive dots. No corporate domain is
 * enforced: ASM-02 records that the prototype's validator accepts any well-formed
 * address, and narrowing that would be a product change, not a fix.
 */
/*
 * Trimmed and lowercased **before** it is validated, not after.
 *
 * The transform used to run last, which meant it never ran at all for the case
 * it existed to handle: `" hamad@boud.ai "` failed `.email()` first and came
 * back as "Enter a valid email address" — for an address that was perfectly
 * valid and had a space on it from a copy-and-paste. Somebody signing in from a
 * pasted address got a message accusing them of mistyping it.
 */
export const emailSchema = z
  .string()
  .transform((v) => v.trim().toLowerCase())
  .pipe(
    z
      .email({ message: 'Enter a valid email address.' })
      .max(254, { message: 'Enter a valid email address.' })
      .refine((v) => !v.includes('..'), { message: 'Enter a valid email address.' }),
  );

/** ISO-8601 with an explicit offset (FRS §8.6.2). */
export const timestampSchema = z.iso.datetime({ offset: true });

/** A date with no time component: YYYY-MM-DD. */
export const dateOnlySchema = z.iso.date();

/** HH:MM on a 24-hour clock. Booking times are snapped to 30-minute steps. */
export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Enter a time as HH:MM.' });

export const uuidSchema = z.uuid();

/**
 * FRS §5 / §8.6.2 — monetary values are integers in whole SAR, never floating
 * point, and carry no formatting. Phase 1 has no monetary fields; this exists so
 * Phase 2 (Billing) cannot reintroduce floats by accident.
 */
export const sarAmountSchema = z.int().nonnegative();

/** Trimmed, non-empty, length-bounded text. */
export function boundedText(max: number, label = 'This field') {
  return z
    .string()
    .trim()
    .min(1, { message: `${label} is required.` })
    .max(max, { message: `${label} must be ${max} characters or fewer.` });
}
